import { useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { formatApiError } from "@/lib/api";
import { useForceDark } from "@/hooks/useForceDark";
import { CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BG = "https://images.unsplash.com/photo-1704633785114-52104ce3d626?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHxmb290YmFsbCUyMHBpdGNoJTIwYWVyaWFsJTIwZ3Jhc3N8ZW58MHx8fHwxNzg3MDYxNjM2fDA&ixlib=rb-4.1.0&q=85";

export default function ForgotPassword() {
  useForceDark();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C] text-white grid md:grid-cols-2">
      <div className="hidden md:block relative overflow-hidden">
        <img src={BG} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0A0F1C] via-[#0A0F1C]/80 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.png" alt="Elsenham YFC" className="w-11 h-11 rounded-full object-contain" />
            <div className="font-display font-black text-lg leading-none tracking-tight">ELSENHAM YFC</div>
          </Link>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Locked out?</div>
            <h1 className="font-display font-black text-5xl mt-3 leading-tight">One<br/>quick<br/>reset.</h1>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-start px-6 md:px-16 py-16">
        {sent ? (
          <div className="w-full max-w-md space-y-6" data-testid="forgot-sent-panel">
            <div className="w-14 h-14 rounded-full bg-[#83B68B]/15 border border-[#83B68B]/40 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-[#83B68B]" />
            </div>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">Check your inbox</div>
              <h2 className="font-display font-black text-4xl mt-2">Reset link sent</h2>
              <p className="mt-4 text-white/70 text-sm leading-relaxed">
                If an account exists for <strong className="text-white">{email}</strong>, we've sent it a link to choose a new password. The link expires in 60 minutes and can only be used once.
              </p>
              <p className="mt-3 text-white/50 text-xs">
                No email? Check spam, then try again in a minute — we don't send more than one link per user per minute.
              </p>
            </div>
            <div className="flex gap-3">
              <Link to="/login" data-testid="forgot-back-to-login" className="btn-volt flex-1 text-center py-3 rounded-md">Back to sign in</Link>
              <button
                type="button"
                onClick={() => { setSent(false); setEmail(""); }}
                data-testid="forgot-try-again"
                className="flex-1 py-3 rounded-md border border-white/20 text-xs tracking-widest uppercase font-display font-bold hover:border-white/40"
              >Use a different email</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="forgot-form">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">Password reset</div>
              <h2 className="font-display font-black text-4xl mt-2">Forgot password?</h2>
              <p className="mt-3 text-white/60 text-sm">Enter the email on your Elsenham YFC account and we'll send you a link to set a new one.</p>
            </div>
            <div>
              <Label htmlFor="email" className="text-xs tracking-widest uppercase text-white/60">Email</Label>
              <Input id="email" type="email" required value={email} onChange={e=>setEmail(e.target.value)} data-testid="forgot-email-input" className="mt-2 bg-[#111827] border-white/10 focus:border-[#83B68B] focus:ring-[#83B68B]/30" />
            </div>
            <button type="submit" disabled={busy || !email} data-testid="forgot-submit-btn" className="btn-volt w-full py-3 rounded-md disabled:opacity-50">
              {busy ? "Sending…" : "Send reset link"}
            </button>
            <div className="text-sm text-white/60 text-center">
              Remembered it?{" "}
              <Link to="/login" className="text-[#83B68B] hover:underline" data-testid="forgot-to-login-link">Back to sign in</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
